import Modal from "@/atomic/atoms/Modal";
import { Invoice } from "@/hooks/useTicket";
import { useSelector } from "react-redux";
import { ReducersTypes } from "@/redux/reducers/Models";
import useI18n from "@/hooks/useI18n";
import { useThemeContext } from "@/contexts/ThemeContext";


const DebtContent = ({ invoice }: { invoice: Invoice | null }) => {
  const { t, Trans } = useI18n();
  const { theme } = useThemeContext();

  
  if (!invoice) {
    return <div style={{ color: theme.colors.text, fontFamily: theme.fonts.primary }}>{t("pages:receipt:withoutDebt")}</div>;
  }

  return (
    <div className="overflow-auto max-h-[calc(100vh-300px)] mt-6 mb-3">
    <p 
      className="text-center font-bold text-xl mb-2"
      style={{ color: theme.colors.text, fontFamily: theme.fonts.primary }}
    >
      {t("pages:receipt:title")}
    </p>
    <p 
      className="text-center text-base"
      style={{ color: theme.colors.text, fontFamily: theme.fonts.primary }}
    >
      <Trans i18nKey="pages:receipt:paymentSuccess" components={[<b></b>]} values={{ price: invoice?.price }} />
    </p>
    <p 
      className="font-bold mt-2"
      style={{ color: theme.colors.text, fontFamily: theme.fonts.primary }}
    >
      {t("pages:receipt:transactionData")} #{invoice?.invoiceId}
    </p>

   
    {invoice?.debts?.map((debt, index) => (
      <div key={index} className="w-full">
        <div className="w-full mt-1 mb-1">
          <p 
            className="font-bold mt-2"
            style={{ color: theme.colors.text, fontFamily: theme.fonts.primary }}
          >
            {debt.plate}
          </p>
          <div 
            className="w-full h-px mt-2 mb-2"
            style={{ backgroundColor: theme.colors.border }}
          />
          <div className="w-full flex flex-row justify-between">
            <p style={{ color: theme.colors.text, fontFamily: theme.fonts.primary }}>{debt.fmtDataTransacao}</p>
          </div>
          <div className="w-full flex flex-row justify-between">
            <p style={{ color: theme.colors.text, fontFamily: theme.fonts.primary }}>{debt.owner}</p>
          </div>
          <div className="w-full flex flex-row justify-between">
            <p style={{ color: theme.colors.text, fontFamily: theme.fonts.primary }}>{debt.nmPraca}</p>
          </div>
          <div 
            className="w-full h-px mt-2 mb-2"
            style={{ backgroundColor: theme.colors.border }}
          />
          <div className="w-full flex flex-row justify-between">
            <p style={{ color: theme.colors.text, fontFamily: theme.fonts.primary }}>{t("pages:receipt:passage")}</p>
            <p style={{ color: theme.colors.text, fontFamily: theme.fonts.primary }}>R$ {debt.originalPrice}</p>
          </div>
          <div className="w-full flex flex-row justify-between">
            <p style={{ color: theme.colors.text, fontFamily: theme.fonts.primary }}>{t("pages:receipt:fines")}</p>
            <p style={{ color: theme.colors.text, fontFamily: theme.fonts.primary }}>R$ {debt.fine || '0,00'}</p>
          </div>
          <div className="w-full flex flex-row justify-between">
            <p 
              className="font-bold"
              style={{ color: theme.colors.text, fontFamily: theme.fonts.primary }}
            >
              {t("pages:receipt:total")}
            </p>
            <p 
              className="font-bold"
              style={{ color: theme.colors.text, fontFamily: theme.fonts.primary }}
            >
              R$ {debt.price}
            </p>
          </div>
        </div>
        <div 
          className="w-full h-px mt-2 mb-2"
          style={{ backgroundColor: theme.colors.border }}
        />
      </div>
    ))}
  </div>
  )
}

const TicketModal = ({
  toggleModal,
  generatePDF,
  toggleModalRegister,
  invoice,
}: {
  toggleModal: () => void;
  toggleModalRegister?: () => void;
  generatePDF: () => void;
  invoice: Invoice | null;
}) => {
  const { t, Trans } = useI18n();
  const customer = useSelector((state: ReducersTypes) => state.customerReducer);
  const { themeName, theme } = useThemeContext();
  const isZapay = themeName === 'zapay';

  const isAuthenticated = !!customer?.client?.id;
  
  if (!isAuthenticated) {
    return (
      <Modal
        content={<div className="flex flex-col">
          <div className="absolute top-3 left-1/2 transform -translate-x-1/2 w-10 h-1 bg-gray-300 rounded-full"></div>
          <button
            type="button"
            onClick={toggleModal}
            className="absolute top-6 right-6 text-black"
          >
            <img src="/svg/icon-close.svg" alt="Close" className="w-6 h-6" />
          </button>
          <DebtContent invoice={invoice} />
          
          {/* Botões específicos para recibo avulso */}
          <div className="flex flex-col gap-4">
            <button
              className="bg-white hover:bg-gray-50 border p-2 h-[calc(60px)] w-full font-medium"
              style={{
                color: isZapay ? theme.colors.primary : '#000000',
                borderColor: isZapay ? theme.colors.primary : '#d1d5db',
                borderRadius: isZapay ? theme.borderRadius.medium : '0.5rem',
                fontFamily: theme.fonts.primary
              }}
              type="button"
              onClick={generatePDF}>
              {t("pages:receipt:downloadReceipt")}
            </button>
            
            {!isZapay && (
              <>
                <div className="text-center">
                  <p 
                    className="text-sm"
                    style={{
                      color: theme.colors.textSecondary,
                      fontFamily: theme.fonts.primary
                    }}
                  >
                    <Trans i18nKey="pages:receipt:downloadReceiptDescription" components={[<b></b>]} />
                  </p>
                </div>
                
                <button
                  className="text-white p-2 h-[calc(60px)] w-full font-medium"
                  style={{
                    backgroundColor: isZapay ? theme.colors.primary : '#000000',
                    borderRadius: isZapay ? theme.borderRadius.medium : '0.5rem',
                    fontFamily: theme.fonts.primary
                  }}
                  type="button"
                  onClick={toggleModalRegister}>
                  {t("pages:receipt:register")}
                </button>
              </>
            )}
          </div>
        </div>}
      />
    );
  }

  return (
    <Modal
      content={<div className="flex flex-col">
        <div className="absolute top-3 left-1/2 transform -translate-x-1/2 w-10 h-1 bg-gray-300 rounded-full"></div>
        <button
          type="button"
          onClick={toggleModal}
          className="absolute top-6 right-6 text-black"
        >
          <img src="/svg/icon-close.svg" alt="Close" className="w-6 h-6" />
        </button>
       
        <DebtContent invoice={invoice} />
        
        <button
          className="text-white border p-2 h-[calc(60px)] w-full font-medium"
          style={{
            backgroundColor: isZapay ? theme.colors.primary : '#000000',
            borderRadius: isZapay ? theme.borderRadius.medium : '0.5rem',
            fontFamily: theme.fonts.primary
          }}
          type="button"
          onClick={generatePDF}>
          Baixar recibo
        </button>
      </div>}
    />
  );
};

export default TicketModal;
